import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { Button, Card, cx, Dialog, Empty, ErrorNote, Field, FreshnessBadge, inputClass, Loading, PageHeader } from '../components/ui';
import { api, unwrap, type AdminOrganization } from '../lib/api';
import { freshness } from '../lib/format';
import { useAdminOrganizations, useMe } from '../lib/queries';
import { InviteForm } from './Members';
import { AddDevice } from './PlantDevices';

const slugify = (s: string) => s.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 63);

type DialogState =
  | { kind: 'org' }
  | { kind: 'plant'; org: AdminOrganization }
  | { kind: 'invite'; org: AdminOrganization }
  | { kind: 'device'; plant: AdminOrganization['plants'][number] }
  | null;

/** Vardex admins: organizations, plants and devices. Metadata only, never sensor data. */
export function Admin() {
  const me = useMe();
  const isAdmin = me.data?.platformRole === 'vardex_admin';
  const orgs = useAdminOrganizations(isAdmin);
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<DialogState>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin-orgs'] });
  const close = () => { setDialog(null); refresh(); };

  if (!isAdmin) return <Empty>Only Vardex admins see this page.</Empty>;
  if (orgs.isPending) return <Loading />;
  if (orgs.error) return <ErrorNote error={orgs.error} />;

  return (
    <>
      <PageHeader
        title="Admin"
        subtitle="Organizations, plants and devices. No sensor data: customers open support access when they want help."
        actions={<Button variant="primary" onClick={() => setDialog({ kind: 'org' })}>New organization</Button>}
      />
      {orgs.data.length === 0 && <Empty>No organizations yet.</Empty>}
      <div className="space-y-4">
        {orgs.data.map((o) => (
          <Card key={o.id} className="p-4">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
              <div>
                <h2 className="text-lg font-semibold">{o.name}</h2>
                <div className="text-sm text-muted">
                  {o.slug} · {o.members} {o.members === 1 ? 'member' : 'members'}
                  {o.pendingInvitations > 0 && ` · ${o.pendingInvitations} invited`}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => setDialog({ kind: 'plant', org: o })}>Add plant</Button>
                <Button onClick={() => setDialog({ kind: 'invite', org: o })}>Invite</Button>
                <Link to={`/members?org=${o.id}`} className="inline-flex items-center rounded-md border border-rule px-3 py-1.5 text-sm font-medium hover:border-muted">Members</Link>
              </div>
            </div>
            {o.plants.length === 0 ? <p className="text-sm text-muted">No plants.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs text-muted">
                    <tr>
                      <th className="py-1.5 pr-3 font-medium">Plant</th>
                      <th className="py-1.5 pr-3 font-medium">Sync</th>
                      <th className="py-1.5 pr-3 font-medium">Devices</th>
                      <th className="py-1.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {o.plants.map((p) => {
                      const f = freshness({ lastHeartbeatAt: p.sync?.lastHeartbeatAt ?? null, dataUntil: p.sync?.watermark ?? null, lagSeconds: p.sync?.lagSeconds ?? null });
                      return (
                        <tr key={p.id} className="border-t border-rule">
                          <td className="py-2 pr-3">
                            <div className="font-medium">{p.name}</div>
                            <div className="text-xs text-muted">{p.slug} · {p.timezone}</div>
                          </td>
                          <td className="py-2 pr-3"><FreshnessBadge state={f.state} label={f.label} /></td>
                          <td className="py-2 pr-3">
                            {p.devices.length === 0 ? <span className="text-muted">none</span> : p.devices.map((d) => (
                              <div key={d.id} className={cx(d.status === 'revoked' && 'text-muted line-through')}>
                                {d.name} <span className="text-xs text-muted">{d.status}{d.hostname ? ` · ${d.hostname}` : ''}</span>
                              </div>
                            ))}
                          </td>
                          <td className="py-2 text-right"><Button variant="ghost" onClick={() => setDialog({ kind: 'device', plant: p })}>Add device</Button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        ))}
      </div>

      <Dialog open={dialog?.kind === 'org'} onClose={close} title="New organization">
        <NewOrganization onDone={close} />
      </Dialog>
      <Dialog open={dialog?.kind === 'plant'} onClose={close} title={dialog?.kind === 'plant' ? `New plant for ${dialog.org.name}` : ''}>
        {dialog?.kind === 'plant' && <NewPlant org={dialog.org} onDone={close} />}
      </Dialog>
      <Dialog open={dialog?.kind === 'invite'} onClose={close} title={dialog?.kind === 'invite' ? `Invite to ${dialog.org.name}` : ''}>
        {dialog?.kind === 'invite' && <InviteForm org={dialog.org.id} plants={dialog.org.plants} onDone={refresh} />}
      </Dialog>
      <Dialog open={dialog?.kind === 'device'} onClose={close} title={dialog?.kind === 'device' ? `Add device to ${dialog.plant.name}` : ''}>
        {dialog?.kind === 'device' && <AddDevice plant={String(dialog.plant.id)} timezone={dialog.plant.timezone} onDone={refresh} />}
      </Dialog>
    </>
  );
}

function NewOrganization({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const create = useMutation({ mutationFn: () => unwrap(api.POST('/admin/organizations', { body: { name, slug: slug || slugify(name) } })), onSuccess: onDone });
  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); create.mutate(); }}>
      <Field label="Name"><input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Erko Seafood AS" required autoFocus /></Field>
      <Field label="Slug" hint="Short id, lowercase. Used in URLs."><input className={inputClass} value={slug || slugify(name)} onChange={(e) => setSlug(e.target.value)} required /></Field>
      <ErrorNote error={create.error} />
      <Button type="submit" variant="primary" className="mt-2" disabled={create.isPending}>Create</Button>
    </form>
  );
}

function NewPlant({ org, onDone }: { org: AdminOrganization; onDone: () => void }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [timezone, setTimezone] = useState('Europe/Oslo');
  const create = useMutation({
    mutationFn: () => unwrap(api.POST('/admin/organizations/{org}/plants', { params: { path: { org: org.id } }, body: { name, slug: slug || slugify(name), timezone } })),
    onSuccess: onDone,
  });
  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); create.mutate(); }}>
      <Field label="Name"><input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="ERKO-STO-01" required autoFocus /></Field>
      <Field label="Slug" hint="Unique across all plants, e.g. the serial number in lowercase."><input className={inputClass} value={slug || slugify(name)} onChange={(e) => setSlug(e.target.value)} required /></Field>
      <Field label="Time zone" hint="Times for this plant are shown in this zone."><input className={inputClass} value={timezone} onChange={(e) => setTimezone(e.target.value)} required /></Field>
      <ErrorNote error={create.error} />
      <Button type="submit" variant="primary" className="mt-2" disabled={create.isPending}>Create</Button>
    </form>
  );
}
